/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { IconField } from "./IconField";
import { SocialSignInButtons } from "./SocialSignInButtons";
import type { GoogleOneTapCredential } from "./googleOneTap";
import type { AuthenticationMode } from "./types";

type EmailStageProps = {
  mode: AuthenticationMode;
  email: string;
  name: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  onModeChange: (mode: AuthenticationMode) => void;
  onEmailChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onBirthMonthChange: (value: string) => void;
  onBirthDayChange: (value: string) => void;
  onBirthYearChange: (value: string) => void;
  onGoogleCredential: (credential: GoogleOneTapCredential) => void;
};

export const EmailStage = defineComponent<EmailStageProps>({
  name: "AuthEmailStage",
  props: [
    "mode",
    "email",
    "name",
    "birthMonth",
    "birthDay",
    "birthYear",
    "onModeChange",
    "onEmailChange",
    "onNameChange",
    "onBirthMonthChange",
    "onBirthDayChange",
    "onBirthYearChange",
    "onGoogleCredential",
  ],
  setup(props) {
    const { t } = useLocalization();
    const months = Array.from({ length: 12 }, (_, index) => ({
      value: String(index + 1),
      label: new Date(2000, index, 1).toLocaleString(undefined, {
        month: "long",
      }),
    }));
    const days = Array.from({ length: 31 }, (_, index) => String(index + 1));

    return () => {
      const isSignup = props.mode === "signup";
      return (
        <div class="space-y-4">
          <SocialSignInButtons
            mode={props.mode}
            onGoogleCredential={props.onGoogleCredential}
          />

          <div class="grid grid-cols-2 gap-1 rounded-xl bg-base-200 p-1">
            {(["signup", "signin"] as AuthenticationMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => props.onModeChange(mode)}
                class={[
                  "rounded-lg py-2 text-sm font-semibold transition-colors",
                  props.mode === mode
                    ? "bg-base-100 text-primary shadow-sm"
                    : "text-base-content",
                ]}
              >
                {mode === "signin"
                  ? t("customer.account.tab_signin")
                  : t("customer.account.tab_signup")}
              </button>
            ))}
          </div>

          <p class="text-sm">
            {isSignup
              ? t("customer.account.signup_subtitle")
              : t("customer.account.signin_subtitle")}
          </p>

          {isSignup && (
            <div class="space-y-3">
              <IconField
                icon="person"
                label={t("customer.account.full_name")}
                placeholder={t("customer.account.name_placeholder")}
                autocomplete="name"
                value={props.name}
                onInput={props.onNameChange}
              />
              <div>
                <span class="mb-1 flex items-center gap-1.5 text-xs font-medium">
                  <span
                    class="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    cake
                  </span>
                  {t("customer.account.birthday")}
                  <span>
                    {t("customer.account.optional")}
                  </span>
                </span>
                <div class="grid grid-cols-[1.4fr_1fr_1fr] gap-2">
                  <select
                    class="select select-bordered w-full rounded-xl focus:select-primary"
                    value={props.birthMonth}
                    onChange={(event) =>
                      props.onBirthMonthChange(
                        (event.target as HTMLSelectElement).value,
                      )
                    }
                  >
                    <option value="">{t("customer.account.month")}</option>
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <select
                    class="select select-bordered w-full rounded-xl focus:select-primary"
                    value={props.birthDay}
                    onChange={(event) =>
                      props.onBirthDayChange(
                        (event.target as HTMLSelectElement).value,
                      )
                    }
                  >
                    <option value="">{t("customer.account.day")}</option>
                    {days.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    inputmode="numeric"
                    maxlength={4}
                    placeholder={t("customer.account.year")}
                    class="input input-bordered w-full rounded-xl focus:input-primary"
                    value={props.birthYear}
                    onInput={(event) =>
                      props.onBirthYearChange(
                        (event.target as HTMLInputElement).value
                          .replace(/\D/g, "")
                          .slice(0, 4),
                      )
                    }
                  />
                </div>
                <p class="mt-1.5 flex items-center gap-1.5 text-xs">
                  <span
                    class="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    redeem
                  </span>
                  {t("customer.account.birthday_hint")}
                </p>
              </div>
            </div>
          )}

          <IconField
            icon="mail"
            label={t("customer.account.email")}
            type="email"
            placeholder="you@example.com"
            autocomplete="email"
            inputmode="email"
            value={props.email}
            onInput={props.onEmailChange}
          />
        </div>
      );
    };
  },
});
